/**
 * Form controls: outlined text fields (56px), text areas, selects, checkboxes
 * and switches. The label sits above the box and is always a real `<label>`;
 * help text and errors are tied to the control with `aria-describedby`, so a
 * screen reader hears the same explanation a sighted person reads.
 */
import { useId, useState } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { Icon, cx } from './primitives.js'
import type { IconName } from './icons.js'
import { useI18n } from '../i18n/context.js'

interface FieldFrame {
  label: ReactNode
  supporting?: ReactNode
  error?: string | null
  counter?: ReactNode
  className?: string
  /** Visually hidden label, for a field whose purpose the surroundings already say. */
  hideLabel?: boolean
}

function Frame({ id, label, supporting, error, counter, className, hideLabel, required, children }: FieldFrame & { id: string; required?: boolean; children: ReactNode }) {
  return <div className={cx('field', error && 'field-invalid', className)}>
    <div className={cx('field-label-row', hideLabel && 'sr-only')}>
      <label className="field-label" htmlFor={id}>{label}{required && <span className="field-required" aria-hidden="true"> *</span>}</label>
      {counter !== undefined && <span className="field-counter">{counter}</span>}
    </div>
    {children}
    {error ? <p className="field-support field-error-text" id={`${id}-support`} role="alert">{error}</p>
      : supporting ? <p className="field-support" id={`${id}-support`}>{supporting}</p> : null}
  </div>
}

export function TextField({ label, supporting, error, counter, className, hideLabel, leadingIcon, mono, id, ...input }: FieldFrame & InputHTMLAttributes<HTMLInputElement> & { leadingIcon?: IconName; mono?: boolean }) {
  const generated = useId()
  const fieldId = id ?? generated
  return <Frame id={fieldId} label={label} supporting={supporting} error={error} counter={counter} className={className} hideLabel={hideLabel} required={input.required}>
    <div className={cx('field-box', leadingIcon && 'has-leading', input.disabled && 'is-disabled')}>
      {leadingIcon && <Icon name={leadingIcon} className="field-leading"/>}
      <input id={fieldId} className={cx('field-input', mono && 'mono')} aria-invalid={error ? true : undefined} aria-describedby={error || supporting ? `${fieldId}-support` : undefined} {...input}/>
    </div>
  </Frame>
}

export function TextArea({ label, supporting, error, counter, className, hideLabel, mono, id, rows = 4, ...input }: FieldFrame & TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }) {
  const generated = useId()
  const fieldId = id ?? generated
  return <Frame id={fieldId} label={label} supporting={supporting} error={error} counter={counter} className={className} hideLabel={hideLabel} required={input.required}>
    <div className={cx('field-box', 'field-box-multiline', input.disabled && 'is-disabled')}>
      <textarea id={fieldId} rows={rows} className={cx('field-input', mono && 'mono')} aria-invalid={error ? true : undefined} aria-describedby={error || supporting ? `${fieldId}-support` : undefined} {...input}/>
    </div>
  </Frame>
}

export function SelectField({ label, supporting, error, className, hideLabel, id, children, ...select }: FieldFrame & SelectHTMLAttributes<HTMLSelectElement>) {
  const generated = useId()
  const fieldId = id ?? generated
  return <Frame id={fieldId} label={label} supporting={supporting} error={error} className={className} hideLabel={hideLabel} required={select.required}>
    <div className={cx('field-box', 'field-box-select', select.disabled && 'is-disabled')}>
      <select id={fieldId} className="field-input" aria-invalid={error ? true : undefined} aria-describedby={error || supporting ? `${fieldId}-support` : undefined} {...select}>{children}</select>
      <Icon name="expand_more" className="field-trailing"/>
    </div>
  </Frame>
}

export function Checkbox({ label, supporting, className, ...input }: { label: ReactNode; supporting?: ReactNode; className?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return <label className={cx('check', input.disabled && 'is-disabled', className)}>
    <input type="checkbox" {...input}/>
    <span className="check-text"><span>{label}</span>{supporting && <small>{supporting}</small>}</span>
  </label>
}

export function Switch({ label, supporting, className, ...input }: { label: ReactNode; supporting?: ReactNode; className?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return <label className={cx('switch', input.disabled && 'is-disabled', className)}>
    <input type="checkbox" role="switch" {...input}/>
    <span className="switch-track" aria-hidden="true"><span className="switch-thumb"/></span>
    <span className="check-text"><span>{label}</span>{supporting && <small>{supporting}</small>}</span>
  </label>
}

/**
 * The "why" that goes into the history, prepared by the interface.
 * Collapsed it shows the sentence that will be recorded; opened, the person
 * can write their own. Empty means "use the prepared one" — never "no reason".
 */
export function ReasonNote({ value, onChange, fallback, disabled, defaultOpen = false }: {
  value: string
  onChange: (value: string) => void
  fallback: string
  disabled?: boolean
  defaultOpen?: boolean
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(defaultOpen || value.trim().length > 0)
  const id = useId()
  const label = t('reasonNote.label')
  return <div className={cx('reason-note', open && 'is-open')}>
    <button type="button" className="reason-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((current) => !current)}>
      <Icon name="edit_note" size={20}/>
      <span className="reason-toggle-text">
        <span className="reason-toggle-label">{label}</span>
        {!open && <span className="reason-preview" title={value.trim() || fallback}>{value.trim() || fallback}</span>}
      </span>
      <Icon name={open ? 'expand_less' : 'expand_more'} size={20}/>
    </button>
    {open && <div id={id} className="reason-body">
      <TextField label={label} hideLabel value={value} onChange={(event) => onChange(event.target.value)} placeholder={fallback} disabled={disabled} supporting={t('reasonNote.hint', { reason: fallback })} maxLength={500}/>
    </div>}
  </div>
}
